import './index.css';
import { useMediaQuery } from '../../hooks/MediaQuery.hook';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Loader from '../../components/common/Loader';
import { Button, Col, Row } from 'antd';
import { ReloadOutlined, SwapOutlined } from '@ant-design/icons';
import PlayerDropdown from './playerDropdown';
import { DIRECT_PLAYERS } from '../../utils/constant';

function buildPlayersList(fixture: any) {
  const links = fixture?.game?.streamerLinks || [];
  const playersList: { websiteLink: string }[] = [];
  let streamSource = '';

  const pushUnique = (websiteLink?: string, preferFirst = false) => {
    if (!websiteLink) return;
    const existingIndex = playersList.findIndex((item) => item.websiteLink === websiteLink);

    if (preferFirst) {
      if (existingIndex >= 0) playersList.splice(existingIndex, 1);
      playersList.unshift({ websiteLink });
      streamSource = websiteLink;
      return;
    }

    if (existingIndex >= 0) return;
    if (!streamSource) streamSource = websiteLink;
    playersList.push({ websiteLink });
  };

  if (fixture?.game?.player2) {
    pushUnique(fixture.game.player2);
  }

  for (const item of links) {
    if (DIRECT_PLAYERS.includes(item?.streamer?.trim()) && item?.websiteLink) {
      pushUnique(item.websiteLink);
    }
  }

  if (fixture?.game?.trendy) {
    pushUnique(fixture.game.trendy, true);
  }

  if (fixture?.game?.websiteLink) {
    pushUnique(fixture.game.websiteLink, true);
  }

  return { streamSource, playersList };
}

function Player({ fixture }: { fixture: any }) {
  const matches = useMediaQuery('(min-width: 768px)');
  const [sourceLink, setSourceLink] = useState('');
  const [iframeKey, setIframeKey] = useState(0);
  const [loader, setLoader] = useState(false);
  const [players, setPlayers] = useState<{ websiteLink: string }[]>([]);
  const [iframeReady, setIframeReady] = useState(false);

  const playerSignature = useMemo(() => {
    const game = fixture?.game;
    if (!game) return '';
    return [
      game.websiteLink || '',
      game.trendy || '',
      game.player2 || '',
      (game.streamerLinks || [])
        .map((link: any) => `${link?.streamer}:${link?.websiteLink}`)
        .join(','),
    ].join('|');
  }, [fixture]);

  useEffect(() => {
    setLoader(true);
    const { streamSource, playersList } = buildPlayersList(fixture);
    setSourceLink((prev) => {
      // Keep current selection if it still exists after refresh
      if (prev && playersList.some((p) => p.websiteLink === prev)) return prev;
      return streamSource;
    });
    setPlayers(playersList);
    setLoader(false);
  }, [fixture, playerSignature]);

  useEffect(() => {
    // Defer iframe mount one frame so header/team UI paints first
    const id = requestAnimationFrame(() => setIframeReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const handlePlayerChange = useCallback((player: { websiteLink: string }) => {
    setSourceLink(player.websiteLink);
    setIframeKey((key) => key + 1);
  }, []);

  const reloadCurrentStream = useCallback(() => {
    setIframeKey((key) => key + 1);
  }, []);

  const switchToNextPlayer = useCallback(() => {
    if (!players.length || !sourceLink) return;
    const currentIndex = players.findIndex((player) => player.websiteLink === sourceLink);
    const nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % players.length;
    handlePlayerChange(players[nextIndex]);
  }, [players, sourceLink, handlePlayerChange]);

  const activePlayerIndex = useMemo(() => {
    if (!sourceLink || !players.length) return 0;
    const index = players.findIndex((player) => player.websiteLink === sourceLink);
    return index >= 0 ? index + 1 : 1;
  }, [players, sourceLink]);

  return (
    <>
      {(matches || players.length < 5) && (
        <div style={{ marginBottom: '5px' }}>
          {players.map((player, index) => (
            <Button
              key={player.websiteLink || index}
              className="time-button"
              style={{ backgroundColor: '#2298ff', marginRight: '5px' }}
              shape="round"
              onClick={() => handlePlayerChange(player)}
              disabled={player.websiteLink === sourceLink}
            >
              Player {index + 1}
            </Button>
          ))}
        </div>
      )}
      {!matches && players.length > 4 && (
        <PlayerDropdown
          players={players}
          sourceLink={sourceLink}
          handlePlayerChange={handlePlayerChange}
        />
      )}
      {sourceLink && (
        <div className="stream-recovery" role="group" aria-label="Stream recovery">
          <div className="stream-recovery-actions">
            <button
              type="button"
              className="stream-recovery-btn stream-recovery-btn-primary"
              onClick={reloadCurrentStream}
            >
              <ReloadOutlined />
              <span>Retry stream</span>
            </button>
            {players.length > 1 && (
              <button
                type="button"
                className="stream-recovery-btn stream-recovery-btn-secondary"
                onClick={switchToNextPlayer}
              >
                <SwapOutlined />
                <span>Next player</span>
              </button>
            )}
          </div>
          <p className="stream-recovery-hint">
            {players.length > 1 ? (
              <>
                Playing <strong>{activePlayerIndex}</strong> of <strong>{players.length}</strong>
                <span className="stream-recovery-sep">·</span>
                Use these if the stream freezes — no page refresh needed
              </>
            ) : (
              <>Stream stuck? Retry before refreshing the page</>
            )}
          </p>
        </div>
      )}
      {sourceLink && iframeReady && (
        <Row
          className={
            matches ? 'video-player-container' : 'video-player-container-mobile'
          }
        >
          <Col span={22}>
            <iframe
              key={iframeKey}
              frameBorder={0}
              marginHeight={0}
              marginWidth={0}
              height={matches ? '600' : '250'}
              width={matches ? '1020' : '350'}
              src={sourceLink}
              allowFullScreen
              scrolling="no"
              title="Live stream player"
              loading="lazy"
            />
            {loader ? <Loader /> : null}
          </Col>
        </Row>
      )}
    </>
  );
}

export default memo(Player);
